import React, { useEffect, useState } from 'react';
import _get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';
import { arrayOf, object } from 'prop-types';
import classNames from 'classnames/bind';
import Tooltip from 'yoda-site-components/lib/components/YodaTooltip/YodaTooltip';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { isDesktopWidth, isTabletWidth } from 'yoda-core-components/lib/helpers/Utils/mediaQuery';
import Slider from 'yoda-core-components/lib/components/Slider/Slider';
import Carousel from 'yoda-site-components/lib/components/Carousel';
import { getDynamicVisNavImageIds } from 'yoda-interfaces/lib/Catalog/CategoriesApi';
import isEmpty from 'lodash/isEmpty';
import { getImageSrc } from 'yoda-core-components/lib/helpers/AssetUtils';
import { dynamicVisNavClicked } from '../../actions/AnalyticsActions';
import { getPageURLFromAPIURL } from '../../utils';
import * as styles from './DynamicVisNav.css';

const cx = classNames.bind(styles);

export const DynamicVisNav = (props) => {
    const dispatch = useDispatch();
    const dynamicVisNavReducer = useSelector((state) => _get(state, 'dynamicVisNavReducer', []));
    /* istanbul ignore next */
    if (!(dynamicVisNavReducer || []).length) return null;
    const products = dynamicVisNavReducer?.filter((product) => product?.imgIdUrl);
    const imageIdUrls = dynamicVisNavReducer?.map((x) => x?.imgIdUrl);

    const [imageIds, setImageIds] = useState([]);
    const [imageHeight, setImageHeight] = useState(0);

    const getImageIds = async (text, imageUrl) => {
        const response = await getDynamicVisNavImageIds(imageUrl);
        const imageIdVal = _get(response, 'data.dynamicVisImg.imgId', '');
        const imageObj = {};
        imageObj.textVal = text;
        imageObj.imageIdVal = imageIdVal;
        setImageIds((previousImgIds) => {
            return [...previousImgIds, imageObj];
        });
    };

    useEffect(() => {
        setImageIds([]);

        if (!isEmpty(imageIdUrls)) {
            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < imageIdUrls.length; i++) {
                const productval = products?.find((product) => {
                    if (product?.imgIdUrl === imageIdUrls[i]) return product;
                    return '';
                });
                getImageIds(productval?.text, imageIdUrls[i]);
            }
        }
    }, [dynamicVisNavReducer]);

    const triggerAnalytics = (productIndex) => {
        const payload = {
            dynamicVisualMapPositionClicked: `position${productIndex + 1}`,
        };
        dispatch(dynamicVisNavClicked(payload));
    };

    /* istanbul ignore next */
    const renderDynamicVisNavLinks = (product, index) => {
        const { apiUrl = '', imgIdUrl = '', text = '' } = product;
        const dynamicVisNavUrl = getPageURLFromAPIURL(apiUrl);
        const imageIdUrl = getPageURLFromAPIURL(imgIdUrl);
        const productIndex = products.findIndex((x) => x.text === text);
        const visualSearchImage = dt([
            'block',
            'border',
            'border-gray-15',
            'border-solid',
            'mx-auto',
            'my-0',
            'rounded-full',
        ]);

        const searchProdName = dt([
            'font-open-sans',
            'mt-1',
            'no-underline',
            'text-center',
            'text-black',
            'text-small',
            'whitespace-normal',
            'pt-1',
        ]);

        const searchLink = `${dt(['inline-block', 'no-underline', 'text-small'])} ${cx(
            'searchLink'
        )}`;

        const searchToolTip = dt([
            'bg-white',
            'border-0',
            'smMd:pt-2',
            'lg:py-2',
            'pr-2',
            'pl-1',
            'rounded-md',
            'shadow-elevation1',
            'bottom-10',
            'right-0',
            'text-center',
            'after:hidden',
        ]);

        const searchTextEllipsis = dt(['max-h-16', 'h-auto', 'overflow-hidden']);
        const result = imageIds.filter((image) => {
            return image.textVal === text;
        });
        let imageIdVal = imageIdUrl;
        if (!isEmpty(result) && result?.length === 1) {
            imageIdVal = result[0].imageIdVal;
        }
        /* istanbul ignore next */
        const renderImage = (
            <img
                alt={text}
                className={visualSearchImage}
                src={getImageSrc(imageIdVal, {}, { context: 'thumbnail' })}
                onLoad={index === 0 ? ({ target }) => setImageHeight(target?.offsetHeight) : null}
            />
        );

        return (
            <a
                href={dynamicVisNavUrl}
                className={searchLink}
                onClick={() => {
                    triggerAnalytics(productIndex);
                }}
            >
                {renderImage}
                {text.length > 28 ? (
                    <Tooltip
                        automationId="dynamic-visnav-tooltip"
                        direction="Topright"
                        renderTooltipContents={text}
                        tooltipContentClass={searchToolTip}
                        tooltipPlacement="Right"
                        triggerEventName="mouseover"
                    >
                        <h4
                            className={`${cx(
                                searchProdName,
                                'searchTextEllipsis'
                            )} ${searchTextEllipsis}`}
                        >
                            {text}
                        </h4>
                    </Tooltip>
                ) : (
                    <h4 className={cx(searchProdName)}>{text}</h4>
                )}
            </a>
        );
    };

    const carouselItemList = (dataItem) => {
        const arrdata = dataItem.map((item, index) => {
            return renderDynamicVisNavLinks(item, index);
        });
        return arrdata;
    };
    /* istanbul ignore next */
    const carouselTheme = (dataItems) => {
        const dataLength = dataItems?.length;
        const isSectionJustifyBetween =
            (isTabletWidth() && dataLength === 5) ||
            (isDesktopWidth() && (dataLength === 6 || dataLength === 7));
        const isSectionJustifyCenter =
            (isTabletWidth() && dataLength <= 4) || (isDesktopWidth() && dataLength <= 5);

        if (isSectionJustifyBetween) {
            return 'dyanmic-rel-visNav-carousel-even';
        }
        if (isSectionJustifyCenter) {
            return 'dynamic-rel-visNav-carousel-center';
        }

        return 'dynamic-rel-visNav-carousel';
    };

    const renderDynamicVisNavList = () => {
        return (
            <Carousel
                theme={carouselTheme(products)}
                automationId="dyvisnavCarousel"
                automationItemId="dyn-viz-nav-carousel"
                children={carouselItemList(products)}
                carouselImageHeight={imageHeight}
            />
        );
    };

    return (
        <article
            className={`${dt(['border-y', 'border-gray-15', 'border-solid', 'py-2', 'mb-6'])} ${cx(
                'dynamicVisNavWrapper'
            )}`}
            data-automation-id="dynamicVisNavGuides"
        >
            <h3
                className={dt([
                    'font-open-sans-bold',
                    'text-center',
                    'text-black',
                    'text-medium',
                    'py-2',
                ])}
            >
                Popular Filters
            </h3>
            <section
                className={`${dt(['py-2', 'overflow-hidden', 'smMd:-mx-6'])} ${cx(
                    'dynamicVisNavSet'
                )}`}
            >
                <Slider contentWrapperClass="visualRelatedContentWrapper">
                    {renderDynamicVisNavList()}
                </Slider>
            </section>
        </article>
    );
};

DynamicVisNav.propTypes = {
    dynamicVisNavReducer: arrayOf(object).isRequired,
};

DynamicVisNav.defaultProps = {
    dynamicVisNavReducer: [],
};

export default DynamicVisNav;
